/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState } from "react";
import { Container, Row, Button } from "react-bootstrap";
import { PlusIcon } from "@heroicons/react/solid";
import withReactContent from "sweetalert2-react-content";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    KanbanCard,
    KanbanList,
    KanbanCreateModal,
    KanbanCopyModal,
    KanbanMoveModal,
    KanbanEditModal
} from "./components";
import { createCard } from "./data";
import Swal from "sweetalert2";

export interface IKanbanWidgetProps {
    kanbanList: any[];
    cardType?: string;
    confirmButtonLabel?: string;
    attributeTypeValue?: any[];
    onKanbanConfirm?(kanbanLists: any): void;
}

const SwalWithBootstrapButtons = withReactContent(
    Swal.mixin({
        customClass: {
            confirmButton: "btn btn-primary me-3",
            cancelButton: "btn btn-gray"
        },
        buttonsStyling: false
    })
);

export const DynamicFieldsKanbanWidget = ({
    kanbanList,
    cardType,
    confirmButtonLabel,
    onKanbanConfirm,
    attributeTypeValue
}: IKanbanWidgetProps) => {
    const [kanbanLists, setKanbanLists] = useState(kanbanList);
    const [showCreateCardModal, setShowCreateCardModal] = useState(false);
    const createCardDefaultProps = { listId: kanbanLists[0].id, cardIndex: 0 };
    const [cardToEdit, setCardToEdit] = useState<any>(null);
    const [cardToCopy, setCardToCopy] = useState<any>(null);
    const [cardToMove, setCardToMove] = useState<any>(null);
    const lCardType = cardType?.toLowerCase();
    const pCardType = `${lCardType}s`;

    const [createCardProps, setCreateCardProps] = useState(
        createCardDefaultProps
    );

    const toggleCreateCardModal = (props = {}) => {
        setCreateCardProps({ ...createCardDefaultProps, ...props });
        setShowCreateCardModal(!showCreateCardModal);
    };

    const handleCopyCard = (card: any = {}) => {
        const { listId, title, attributeType, isRequired } = card;
        const listsUpdated = createCardInListAtIndex({
            listId,
            title,
            attributeType,
            isRequired
        });

        setCardToCopy(null);
        setKanbanLists(listsUpdated);
    };

    const handleCardChange = (props: any) => {
        const { listId, cardId, ...otherProps } = props;
        const listsUpdated = kanbanLists.map(l => {
            if (l.id !== listId) return l;

            const cards = l.cards.map((c: any) =>
                c.id === cardId ? { ...c, ...otherProps } : c
            );
            return { ...l, cards };
        });

        // if (cardToEdit) {
        //   setCardToEdit({ ...cardToEdit, ...otherProps });
        // }
        setCardToEdit(null);
        setKanbanLists(listsUpdated);
        onKanbanConfirm && onKanbanConfirm(listsUpdated);
    };

    const handleListTitleChange = ({ id, title }: any) => {
        const listsUpdated = kanbanLists.map(l =>
            l.id === id ? { ...l, title } : l
        );
        setKanbanLists(listsUpdated);
    };

    const removeCardsFromList = (cards: any) => {
        const cardsGroupedByListId = cards.reduce((acc: any, card: any) => {
            const { listId, cardId } = card;

            if (!acc[listId]) acc[listId] = [cardId];
            else acc[listId].push(cardId);

            return acc;
        }, {});

        const listsUpdated = kanbanLists.map(l => {
            const cardsToDelete = cardsGroupedByListId[l.id];
            if (!cardsToDelete) return l;

            const cardsUpdated = l.cards.filter(
                (c: any) => !cardsToDelete.includes(c.id)
            );
            return { ...l, cards: cardsUpdated };
        });

        return listsUpdated;
    };

    const handleKanbanConfirm = () => {
        console.log("kanbanList =>", kanbanList);
        if (kanbanLists && kanbanLists[0]?.cards?.length) {
            onKanbanConfirm && onKanbanConfirm(kanbanLists);
        } else {
            SwalWithBootstrapButtons.fire({
                icon: "error",
                title: `No ${cardType} found`,
                text: `Please add at least one attribute to proceed`,
                showCancelButton: false,
                confirmButtonText: "OK"
            });
        }
    };

    const handleCardsDelete = async (cards: any = []) => {
        const listsUpdated = removeCardsFromList(cards);
        setKanbanLists(listsUpdated);
        onKanbanConfirm && onKanbanConfirm(listsUpdated);
    };

    const handleListDelete = async (listId: any) => {
        const result = await SwalWithBootstrapButtons.fire({
            icon: "error",
            title: "Confirm deletion",
            text: "Are you sure do you want to delete this list?",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "Cancel"
        });

        if (result.isConfirmed) {
            const listsUpdated = kanbanLists.filter(l => l.id !== listId);
            setKanbanLists(listsUpdated);

            await SwalWithBootstrapButtons.fire(
                "Deleted",
                "The list has been deleted.",
                "success"
            );
        }
    };

    const createCardInListAtIndex = (props: any) => {
        const { listId, cardIndex, ...otherProps } = props;

        const listsUpdated = kanbanLists.map(l => {
            if (listId !== l.id) return l;

            const newCard = createCard(otherProps);
            l.cards.splice(cardIndex, 0, newCard);

            return l;
        });
        return listsUpdated;
    };

    const handleCreateCard = (props = {}) => {
        const listsUpdated = createCardInListAtIndex({
            ...createCardProps,
            ...props
        });
        toggleCreateCardModal();
        setKanbanLists(listsUpdated);
    };

    const reorderCards = (cards = [], startIndex: any, endIndex: any) => {
        const [cardRemoved] = cards.splice(startIndex, 1);
        cards.splice(endIndex, 0, cardRemoved);

        return cards;
    };

    const moveCardFromList = (
        sList: any,
        dList: any,
        sIndex: any,
        dIndex: any
    ) => {
        const sCards = [...sList.cards];
        const dCards = [...dList.cards];

        const [cardRemoved] = sCards.splice(sIndex, 1);
        dCards.splice(dIndex, 0, cardRemoved);

        return [
            { ...sList, cards: sCards },
            { ...dList, cards: dCards }
        ];
    };

    const handleDragEnd = (dragResult: any) => {
        const { source, destination } = dragResult;

        //  dropped outside the list
        if (!destination) {
            return;
        }

        const { droppableId: sListId, index: sCardIndex } = source;
        const { droppableId: dListId, index: dCardIndex } = destination;

        const sList = kanbanLists.find(l => l.id === sListId);
        const dList = kanbanLists.find(l => l.id === dListId);

        if (sListId === dListId) {
            // reorder cards in the list only if card's index changes
            if (sCardIndex !== dCardIndex) {
                const sCardsUpdated = reorderCards(
                    sList.cards,
                    sCardIndex,
                    dCardIndex
                );
                const listsUpdated = kanbanLists.map(l =>
                    l.id === sListId ? { ...l, cards: sCardsUpdated } : l
                );
                setKanbanLists(listsUpdated);
            }
        } else {
            const [sListUpdated, dListUpdated] = moveCardFromList(
                sList,
                dList,
                sCardIndex,
                dCardIndex
            );
            const listsUpdated = kanbanLists.map(l =>
                l.id === sListId
                    ? sListUpdated
                    : l.id === dListId
                    ? dListUpdated
                    : l
            );
            setKanbanLists(listsUpdated);
        }

        if (cardToMove) {
            setCardToMove(null);
        }
    };
    const getCardStyle = (style: any, snapshot: any) => {
        const isJustDragging = snapshot.isDragging && !snapshot.isDropAnimating;

        if (!isJustDragging) {
            return style;
        }

        return {
            ...style,
            transform: `${style.transform || ""} rotate(6deg)`
        };
    };

    const _renderKanbanCreateModalIf = () => {
        if (showCreateCardModal) {
            return (
                <KanbanCreateModal
                    show={showCreateCardModal}
                    onHide={toggleCreateCardModal}
                    onSubmit={handleCreateCard}
                    attributeTypeValue={attributeTypeValue}
                    cardType={cardType}
                />
            );
        }
    };

    const _renderKanbanCopyModalIf = () => {
        if (cardToCopy) {
            return (
                <KanbanCopyModal
                    cardType={cardType}
                    show={true}
                    {...cardToCopy}
                    lists={kanbanLists}
                    onHide={() => setCardToCopy(null)}
                    onSubmit={handleCopyCard}
                />
            );
        }
    };

    const _renderKanbanMoveModalIf = () => {
        if (cardToMove) {
            return (
                <KanbanMoveModal
                    show={true}
                    {...cardToMove}
                    lists={kanbanLists}
                    onHide={() => setCardToMove(null)}
                    onSubmit={handleDragEnd}
                />
            );
        }
    };

    const _renderKanbanEditModalIf = () => {
        if (cardToEdit) {
            return (
                <KanbanEditModal
                    show={true}
                    {...cardToEdit}
                    onHide={() => setCardToEdit(null)}
                    onChange={handleCardChange}
                    attributeTypeValue={attributeTypeValue}
                />
            );
        }
    };

    return (
        <>
            {_renderKanbanCreateModalIf()}
            {_renderKanbanCopyModalIf()}
            {_renderKanbanMoveModalIf()}
            {_renderKanbanEditModalIf()}
            <Container fluid className="kanban-container py-4 px-0">
                <Row className="d-flex flex-nowrap align-content-start align-items-start justify-content-start">
                    <DragDropContext onDragEnd={handleDragEnd}>
                        {kanbanLists.map((list, ind) => {
                            const { id: listId, cards } = list;

                            return (
                                <Droppable
                                    index={ind}
                                    droppableId={`${listId}`}
                                    key={`kanban-list-${listId}`}
                                >
                                    {provided => {
                                        const {
                                            innerRef: listRef,
                                            placeholder,
                                            droppableProps
                                        } = provided;

                                        return (
                                            <KanbanList
                                                {...list}
                                                listRef={listRef}
                                                extraProps={droppableProps}
                                            >
                                                {cards.map(
                                                    (card: any, index: any) => {
                                                        const {
                                                            id: cardId
                                                        } = card;

                                                        return (
                                                            <Draggable
                                                                index={index}
                                                                draggableId={`${cardId}`}
                                                                key={`kanban-card-${cardId}`}
                                                            >
                                                                {(
                                                                    provided,
                                                                    snapshot
                                                                ) => {
                                                                    const {
                                                                        innerRef: cardRef,
                                                                        draggableProps,
                                                                        dragHandleProps
                                                                    } = provided;

                                                                    return (
                                                                        <KanbanCard
                                                                            {...card}
                                                                            cardRef={
                                                                                cardRef
                                                                            }
                                                                            style={getCardStyle(
                                                                                draggableProps.style,
                                                                                snapshot
                                                                            )}
                                                                            extraProps={{
                                                                                ...draggableProps,
                                                                                ...dragHandleProps
                                                                            }}
                                                                            onDelete={() =>
                                                                                handleCardsDelete(
                                                                                    [
                                                                                        {
                                                                                            listId,
                                                                                            cardId
                                                                                        }
                                                                                    ]
                                                                                )
                                                                            }
                                                                            onCompChange={(
                                                                                id,
                                                                                value
                                                                            ) =>
                                                                                handleCardChange(
                                                                                    {
                                                                                        listId,
                                                                                        cardId,
                                                                                        value
                                                                                    }
                                                                                )
                                                                            }
                                                                        />
                                                                    );
                                                                }}
                                                            </Draggable>
                                                        );
                                                    }
                                                )}

                                                {placeholder}
                                                <Button
                                                    variant="outline-gray-500"
                                                    onClick={() =>
                                                        toggleCreateCardModal({
                                                            listId,
                                                            cardIndex:
                                                                cards.length
                                                        })
                                                    }
                                                    className="d-inline-flex align-items-center justify-content-center dashed-outline new-card w-100"
                                                    style={{
                                                        marginLeft: "15px"
                                                    }}
                                                >
                                                    <PlusIcon className="icon icon-xs me-2" />{" "}
                                                    {cards?.length
                                                        ? `Add another ${lCardType}`
                                                        : `Add a new ${lCardType}`}
                                                </Button>
                                            </KanbanList>
                                        );
                                    }}
                                </Droppable>
                            );
                        })}
                    </DragDropContext>
                </Row>
            </Container>
        </>
    );
};

DynamicFieldsKanbanWidget.defaultProps = {
    cardType: "Attribute",
    confirmButtonLabel: "Next"
};
